import type { PrometheusSample } from '../types';

/**
 * Parse Prometheus text exposition format into a flat list of samples.
 * Comments (# HELP, # TYPE) are skipped. Lines that fail to parse are dropped.
 *
 * Example line:
 *   http_requests_total{method="POST",status="200"} 1027 1395066363000
 */
export function parsePrometheusText(text: string): PrometheusSample[] {
  const samples: PrometheusSample[] = [];
  if (!text || typeof text !== 'string') return samples;

  const lines = text.split('\n');
  for (const raw of lines) {
    const line = raw.trim();
    if (!line || line.startsWith('#')) continue;

    const labelStart = line.indexOf('{');
    let name = '';
    let labelsBlock = '';
    let rest = '';

    if (labelStart === -1) {
      const spaceIdx = line.indexOf(' ');
      if (spaceIdx === -1) continue;
      name = line.slice(0, spaceIdx);
      rest = line.slice(spaceIdx + 1);
    } else {
      name = line.slice(0, labelStart);
      const labelEnd = line.indexOf('}', labelStart);
      if (labelEnd === -1) continue;
      labelsBlock = line.slice(labelStart + 1, labelEnd);
      rest = line.slice(labelEnd + 1).trim();
    }

    const valueStr = rest.split(/\s+/)[0];
    const value = Number(valueStr);
    if (!Number.isFinite(value)) continue;

    const labels: Record<string, string> = {};
    if (labelsBlock) {
      const labelRegex = /([a-zA-Z_][a-zA-Z0-9_]*)="((?:[^"\\]|\\.)*)"/g;
      let m: RegExpExecArray | null;
      while ((m = labelRegex.exec(labelsBlock)) !== null) {
        labels[m[1]] = m[2].replace(/\\"/g, '"').replace(/\\\\/g, '\\');
      }
    }

    samples.push({ name, value, labels });
  }
  return samples;
}

/** Sum all samples with a given metric name (after optional label filter). */
export function sumMetric(
  samples: PrometheusSample[],
  metricName: string,
  labelFilter?: Record<string, string>,
): number {
  return samples
    .filter((s) => s.name === metricName)
    .filter((s) => {
      if (!labelFilter) return true;
      return Object.entries(labelFilter).every(([k, v]) => s.labels[k] === v);
    })
    .reduce((acc, s) => acc + s.value, 0);
}

/** Group samples by a label key and return Map<labelValue, totalValue>. */
export function groupMetric(
  samples: PrometheusSample[],
  metricName: string,
  groupLabel: string,
): Record<string, number> {
  const out: Record<string, number> = {};
  for (const s of samples) {
    if (s.name !== metricName) continue;
    const key = s.labels[groupLabel] ?? '_';
    out[key] = (out[key] || 0) + s.value;
  }
  return out;
}
